import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, Star, Wallet, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const PLANS = [
  {
    id: 'starter', name: 'Starter', price: 29, color: G,
    icon: <Star size={22}/>, popular: false,
    features: [
      '3 simultaneous trades',
      '3 currency pairs',
      'Basic risk management',
      'Email notifications',
      '24/7 bot trading',
    ],
  },
  {
    id: 'pro', name: 'Pro', price: 59, color: P2,
    icon: <Zap size={22}/>, popular: true,
    features: [
      '10 simultaneous trades',
      '10 currency pairs',
      'Advanced risk management',
      'Telegram notifications',
      '24/7 bot trading',
      'Priority support',
      'Trade analytics',
    ],
  },
  {
    id: 'elite', name: 'Elite', price: 99, color: C,
    icon: <Crown size={22}/>, popular: false,
    features: [
      '50 simultaneous trades',
      '30 currency pairs',
      'Advanced risk management',
      'Telegram + Email alerts',
      '24/7 bot trading',
      'VIP support',
      'Full trade analytics',
      'Custom strategies',
      'News guard protection',
    ],
  },
];

const FAQ = [
  { q: 'How do I pay for subscription?',  a: 'Deposit USDT to your Kudibot wallet. Subscription is automatically deducted!' },
  { q: 'Can I cancel anytime?',            a: 'Yes! Cancel auto-renew anytime. Your plan stays active until the expiry date.' },
  { q: 'What happens when plan expires?', a: 'Bot stops trading. Deposit USDT and renew to continue trading!' },
  { q: 'Can I upgrade my plan?',           a: 'Yes! Subscribe to a higher plan anytime. New plan starts immediately!' },
  { q: 'Is my trading account safe?',      a: 'Yes! We use read-only investor password. We cannot withdraw your funds!' },
  { q: 'What currency pairs are traded?', a: 'EURUSD, GBPUSD, AUDUSD, USDJPY and more depending on your plan!' },
];

export default function Pricing() {
  const { isAuthenticated } = useAuthStore();
  const [loading,    setLoading]    = useState(null);
  const [myPlan,     setMyPlan]     = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) loadMyPlan();
  }, [isAuthenticated]);

  const loadMyPlan = async () => {
    try {
      const res = await api.get('/subscriptions/my-plan');
      setMyPlan(res.data);
    } catch { /* no plan */ }
  };

  const subscribe = async (planId) => {
    if (!isAuthenticated) { navigate('/register'); return; }
    setLoading(planId);
    try {
      const res = await api.post('/subscriptions/subscribe', { plan: planId });
      toast.success(res.data.message || 'Subscribed successfully!');
      await loadMyPlan();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed');
    } finally {
      setLoading(null);
    }
  };

  const cancelAutoRenew = async () => {
    setCancelling(true);
    try {
      const res = await api.post('/subscriptions/cancel');
      toast.success(res.data.message);
      await loadMyPlan();
    } catch {
      toast.error('Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const renewPlan = async () => {
    setCancelling(true);
    try {
      await api.post('/subscriptions/renew');
      toast.success('Plan renewed!');
      await loadMyPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renewal failed');
    } finally {
      setCancelling(false);
    }
  };

  const isCurrent = (planId) => myPlan?.plan === planId && myPlan?.status === 'active';

  return (
    <Layout>
      <div style={{ padding: '28px 28px 48px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 14px', borderRadius: 20,
            background: `${P}12`, border: `1px solid ${P}30`,
            color: P2, fontSize: 12, fontWeight: 700, marginBottom: 16,
          }}>
            <Zap size={12}/> Simple Pricing
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, color: '#fff', margin: '0 0 10px', letterSpacing: -1 }}>
            Choose Your Trading Plan
          </h1>
          <p style={{ fontSize: 15, color: '#64748B', maxWidth: 480, margin: '0 auto' }}>
            All plans include full stablecoin collection & payout access. Upgrade or cancel anytime.
          </p>
        </motion.div>

        {/* Active Plan Banner */}
        {isAuthenticated && myPlan?.status === 'active' && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ ...CARD, padding: '16px 20px', marginBottom: 24, border: `1px solid ${G}30`, background: `${G}06` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: `${G}14`, border: `1px solid ${G}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Check size={20} style={{ color: G }}/>
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 2px', textTransform: 'capitalize' }}>
                    {myPlan.plan} Plan Active
                  </p>
                  <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>
                    {myPlan.days_left} days remaining ·{' '}
                    {myPlan.auto_renew ? 'Auto-renews' : 'No auto-renew'} ·{' '}
                    Balance: ${myPlan.balance?.toFixed(2)} USDT
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {myPlan.auto_renew ? (
                  <motion.button whileHover={{ scale: 1.02 }} onClick={cancelAutoRenew} disabled={cancelling}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                      background: `${R}10`, border: `1px solid ${R}30`,
                      color: R, fontSize: 13, fontWeight: 600,
                    }}>
                    <X size={13}/> Cancel Auto-Renew
                  </motion.button>
                ) : (
                  <motion.button whileHover={{ scale: 1.02 }} onClick={renewPlan} disabled={cancelling}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                      background: `${G}10`, border: `1px solid ${G}30`,
                      color: G, fontSize: 13, fontWeight: 600,
                    }}>
                    <RefreshCw size={13}/> Enable Auto-Renew
                  </motion.button>
                )}
                <Link to="/dashboard" style={{ textDecoration: 'none' }}>
                  <motion.button whileHover={{ scale: 1.02 }}
                    style={{
                      padding: '8px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
                      background: `linear-gradient(135deg,${P},${P2})`,
                      color: '#000', fontSize: 13, fontWeight: 700,
                    }}>
                    Dashboard →
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Low balance warning */}
        {isAuthenticated && myPlan && myPlan.balance < 29 && myPlan.status !== 'active' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ ...CARD, padding: '14px 18px', marginBottom: 20, background: '#F59E0B0A', border: '1px solid #F59E0B30' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Wallet size={16} style={{ color: '#F59E0B' }}/>
              <p style={{ fontSize: 13, color: '#F59E0B', margin: 0 }}>
                Your balance is <strong>${myPlan.balance?.toFixed(2)} USDT</strong>. Deposit more USDT to subscribe.{' '}
                <Link to="/wallet" style={{ color: P2, fontWeight: 700 }}>Deposit Now →</Link>
              </p>
            </div>
          </motion.div>
        )}

        {/* Plans Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginBottom: 48 }} className="pricing-grid">
          {PLANS.map((plan, i) => (
            <motion.div key={plan.id}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                ...CARD, padding: 28,
                position: 'relative',
                border: isCurrent(plan.id) ? `2px solid ${plan.color}` :
                        plan.popular ? `1px solid ${plan.color}30` : '1px solid rgba(255,255,255,0.06)',
                boxShadow: isCurrent(plan.id) ? `0 0 32px ${plan.color}20` :
                           plan.popular ? `0 0 24px ${plan.color}10` : 'none',
                transform: plan.popular ? 'scale(1.03)' : 'none',
              }}>

              {plan.popular && !isCurrent(plan.id) && (
                <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                  <span style={{
                    padding: '4px 16px', borderRadius: 20, fontSize: 11, fontWeight: 800, color: '#000',
                    background: `linear-gradient(135deg,${plan.color},${plan.color}90)`,
                  }}>MOST POPULAR</span>
                </div>
              )}
              {isCurrent(plan.id) && (
                <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                  <span style={{
                    padding: '4px 16px', borderRadius: 20, fontSize: 11, fontWeight: 800, color: '#000',
                    background: G,
                  }}>✓ CURRENT PLAN</span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: `${plan.color}18`, color: plan.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{plan.icon}</div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: plan.color, margin: 0 }}>{plan.name}</h3>
                  {isCurrent(plan.id) && <p style={{ fontSize: 11, color: '#64748B', margin: 0 }}>{myPlan.days_left} days left</p>}
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontSize: 42, fontWeight: 900, color: '#fff', letterSpacing: -2 }}>${plan.price}</span>
                  <span style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>/month</span>
                </div>
                <p style={{ fontSize: 11, color: '#334155', margin: '4px 0 0' }}>Paid in USDT from wallet</p>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan.features.map((f, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#CBD5E1' }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                      background: plan.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={11} color="#000"/>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrent(plan.id) ? (
                <div style={{
                  width: '100%', padding: '12px 0', borderRadius: 10, textAlign: 'center',
                  fontSize: 13, fontWeight: 700, background: `${plan.color}14`,
                  border: `1px solid ${plan.color}30`, color: plan.color,
                }}>
                  ✓ Active Plan
                </div>
              ) : (
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => subscribe(plan.id)} disabled={loading === plan.id}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg,${plan.color},${plan.color}99)`,
                    color: '#000', fontSize: 13, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {loading === plan.id
                    ? <div style={{ width: 18, height: 18, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
                    : isAuthenticated
                    ? `Subscribe — $${plan.price} USDT`
                    : 'Get Started'
                  }
                </motion.button>
              )}
            </motion.div>
          ))}
        </div>

        {/* FAQ */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 28px', letterSpacing: -0.5 }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="faq-grid">
            {FAQ.map((faq, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.08 }}
                style={{ ...CARD, padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>{faq.q}</h4>
                <p style={{ fontSize: 13, color: '#64748B', margin: 0, lineHeight: 1.6 }}>{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>

      <style>{`
        @media (max-width: 900px) {
          .pricing-grid { grid-template-columns: 1fr !important; }
          .faq-grid     { grid-template-columns: 1fr !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
}
